import React, { useCallback, useEffect, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { Archive, CircleArrowDown, Eye, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/src/components/ui/button";
import { contentFiltersToApi } from "@/src/utils/contentFiltersToApi";
import { SORT_DIRECTION } from "@/src/utils/sortingToApi";

import ContentListButtonMenu from "./ContentListButtonMenu";
import ContentListQuota from "./ContentListQuota";
import { CONTENT_TYPE_ICONS } from "./contentTypeIcons";
import {
  contentColumns,
  getContentHref,
  getPubStatus,
} from "./tableParams/contentColumns";
import { CONTENT_TYPE } from "./Type";
import { notifError } from "../../actions/FlashMessageActions";
import { delApi, postApi } from "../../actions/SendActions";
import MediaAPI from "../../api/MediaAPI";
import StatsAPI from "../../api/StatsAPI";
import { EDITOR_ADMIN, OWNER, PROJECT_ADMIN } from "../../constants/roles";
import useAbility from "../../hooks/useAbility";
import useDataTableQuery from "../../hooks/useDataTableQuery";
import useDataTableServerState from "../../hooks/useDataTableServerState";
import { transformMediasIndex } from "../../utils/data/Content";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import LiveCalendar from "../timeline/LiveCalendar";

import type { JSX, ReactNode } from "react";

import type { ContentListRow } from "./tableParams/contentColumns.types";
import type { GetMediaListResponse } from "../../api/MediaAPI.types";
import type { MediaType } from "../../constants/media";
import type { AppState } from "../../reducers";
import type { DataTablePage, RowAction } from "../dataTable/dataTable.types";

interface ContentDeletionWarningProps {
  body: ReactNode;
}

function ContentDeletionWarning({
  body,
}: ContentDeletionWarningProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  return (
    <React.Fragment>
      <p className="mb-1.25">{body}</p>
      <p>
        {t("warning_1")} <strong>{t("warning_2_bold")}</strong>.{" "}
        {t("warning_3")}
      </p>
    </React.Fragment>
  );
}

function selectContentPage(
  response: GetMediaListResponse,
): DataTablePage<ContentListRow> {
  // Copie : transformMediasIndex complète les lignes en place.
  const rowList: ContentListRow[] = transformMediasIndex({
    data: response.data.map((content) => ({
      ...content,
      pub_status: getPubStatus(content),
    })),
  }).data;

  return { rows: rowList, rowCount: response.page.total_items };
}

function ContentList(): JSX.Element {
  const { t } = useTranslation("editor-content");
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const history = useHistory();

  const { type: paramsType, projectSlug } = useParams<{
    type: MediaType;
    projectSlug: string;
  }>();

  const { hasRole, canAccess } = useAbility();

  const contentType = Object.values(CONTENT_TYPE).includes(paramsType)
    ? paramsType
    : CONTENT_TYPE.HOSTED;

  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const isLiveOkastAllowed = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options
        ?.allow_live_okast,
  );
  const isFetchingOrgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config.isFetching,
  );
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const hasFastOption = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_fast,
  );

  const isLive = contentType === CONTENT_TYPE.LIVE;
  const isHosted = contentType === CONTENT_TYPE.HOSTED;
  const isExternal = contentType === CONTENT_TYPE.EXTERNAL;

  const hasCuepoints = Boolean(hasFastOption) && isHosted;
  const hasTypeColumn = isHosted || isExternal;

  const { contentsWrite: canAccessContentsWrite } = canAccess(orgaOptions);
  const ownerRole = hasRole([OWNER, EDITOR_ADMIN, PROJECT_ADMIN]);

  const handleUpdateContent = useCallback((): void => {
    queryClient.invalidateQueries([MediaAPI.getMediaList.name]);
    queryClient.invalidateQueries([MediaAPI.getFastIndexMediaList.name]);
  }, [queryClient]);

  const handleArchive = useCallback(
    (uuid: string): void => {
      dispatch(
        postApi({
          endpoint: `/api/media/v6/admin/medias/${uuid}/archive`,
          type: "ARCHIVE_INDEX_MEDIA",
          item: "index_medias",
          token,
          message: t("editor-notif:archiveSuccess"),
          error: t("editor-notif:archiveError"),
          callback: handleUpdateContent,
        }),
      );
    },
    [dispatch, token, t, handleUpdateContent],
  );

  const handleDelete = useCallback(
    (uuid: string): void => {
      dispatch(
        delApi({
          endpoint: `/api/media/v6/admin/medias/${uuid}/delete`,
          type: "DELETE_INDEX_MEDIA",
          item: "index_medias",
          headers: [{ key: "xx-ok-project", value: projectSlug }],
          token,
          message: t("editor-notif:deleteSuccess"),
          callbackError: () => {
            dispatch(notifError(t("editor-notif:deleteError")));
          },
          callback: handleUpdateContent,
        }),
      );
    },
    [dispatch, projectSlug, token, t, handleUpdateContent],
  );

  // `staleTime: 0` : le cache de l'app (10 min) servirait un ancien statut.
  const loadActions = useCallback(
    async (content: ContentListRow): Promise<RowAction[]> => {
      const status = await queryClient.fetchQuery({
        ...MediaAPI.generateQuery({
          fn: MediaAPI.getMediaStatus,
          params: [{ uuid: content.uuid, projectSlug }],
        }),
        staleTime: 0,
      });
      const canEdit = status.is_editable && canAccessContentsWrite;

      return [
        {
          label: canEdit ? t("edit_action") : t("see_more"),
          icon: canEdit ? <Pencil /> : <Eye />,
          onClick: () =>
            history.push(getContentHref(contentType, content.uuid)),
        },
        ...(status.is_archivable
          ? [
              {
                label: t("archive"),
                icon: <Archive />,
                confirm: {
                  title: t("archive"),
                  description: (
                    <ContentDeletionWarning
                      body={t("archiveBody", { content: status.label })}
                    />
                  ),
                  confirmLabel: t("confirm"),
                },
                onClick: () => handleArchive(content.uuid),
              } satisfies RowAction,
            ]
          : []),
        ...(status.is_deletable
          ? [
              {
                label: t("delete"),
                icon: <Trash2 />,
                variant: "destructive",
                confirm: {
                  title: t("deleteTitle"),
                  description: (
                    <ContentDeletionWarning
                      body={t("deleteBody", { content: status.label })}
                    />
                  ),
                  confirmLabel: t("confirm"),
                },
                onClick: () => handleDelete(content.uuid),
              } satisfies RowAction,
            ]
          : []),
      ];
    },
    [
      queryClient,
      projectSlug,
      canAccessContentsWrite,
      t,
      history,
      contentType,
      handleArchive,
      handleDelete,
    ],
  );

  const columnList = useMemo(
    () =>
      contentColumns({
        t,
        contentType,
        isLive,
        isHosted,
        hasTypeColumn,
        hasCuepoints,
        ownerRole,
        loadActions,
      }),
    [
      t,
      contentType,
      isLive,
      isHosted,
      hasTypeColumn,
      hasCuepoints,
      ownerRole,
      loadActions,
    ],
  );

  const serverState = useDataTableServerState<ContentListRow>({
    columns: columnList,
    defaultSorting: { id: "updated_at", desc: true },
  });

  const mediaListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediaList,
    params: [
      {
        page: serverState.page,
        perPage: serverState.perPage,
        filter: contentFiltersToApi(serverState.filters, contentType),
        order: serverState.order ?? {
          field: "updated_at",
          direction: SORT_DIRECTION.DESC,
        },
        search: serverState.search || null,
      },
    ],
  });

  const contentTable = useDataTableQuery(mediaListQuery, selectContentPage);

  const titleLabel = useMemo(() => {
    switch (contentType) {
      case CONTENT_TYPE.LIVE:
        return t("liveTitle");
      case CONTENT_TYPE.HOSTED:
        return t("hostedTitle");
      case CONTENT_TYPE.EXTERNAL:
        return t("externalTitle");
      case CONTENT_TYPE.FAST:
        return t("fastTitle");
      default:
        return "";
    }
  }, [contentType, t]);

  const isLiveForbidden =
    contentType === "live" && !isLiveOkastAllowed && !isFetchingOrgaConfig;

  useEffect(() => {
    if (isLiveForbidden) history.push("/");
  }, [isLiveForbidden, history]);

  const handleExportMediasRights = async (): Promise<void> => {
    try {
      const dataMediasRights = await StatsAPI.getExportMediasRights({
        isExport: true,
      });
      const csv = new File(
        [dataMediasRights as BlobPart],
        "expired_rights.csv",
        { type: "text/csv" },
      );
      saveAs(csv);
    } catch (error) {
      toast.error(t("genericError", { ns: "error" }));
      console.error("Error exporting expired rights", error);
    }
  };

  const isQuotaDisplayed = (isLive || isHosted) && contentTable.data.length > 0;

  const headerActions = (
    <React.Fragment>
      {isHosted ? (
        <Button onClick={handleExportMediasRights} size="lg" type="button">
          <CircleArrowDown />
          {t("expiredRights")}
        </Button>
      ) : null}
      <ContentListButtonMenu contentType={contentType} />
    </React.Fragment>
  );

  const emptyState = (
    <DataTableEmptyState
      action={<ContentListButtonMenu contentType={contentType} />}
      icon={CONTENT_TYPE_ICONS.video}
      message={t("createContentText")}
      title={titleLabel}
    />
  );

  return (
    <CappedPage contentClassName="py-15">
      {isLive ? <LiveCalendar /> : null}
      {/* Une route sert tous les types : la clé redonne un tableau neuf à chaque type. */}
      <DataTable
        key={contentType}
        columns={columnList}
        data={contentTable.data}
        description={isQuotaDisplayed ? <ContentListQuota /> : undefined}
        emptyState={emptyState}
        getRowHref={(content) => getContentHref(contentType, content.uuid)}
        headerActions={headerActions}
        isError={contentTable.isError}
        isLoading={contentTable.isLoading}
        onRetry={contentTable.onRetry}
        rowCount={contentTable.rowCount}
        serverState={serverState}
        storageKey={`contents-${contentType}`}
        title={titleLabel}
      />
    </CappedPage>
  );
}

export default ContentList;
